import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {Promise<unknown[]> | null} */
  #cartModulesPromise = null;

  /** @type {boolean} */
  #celebrationPending = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    trigger?.addEventListener('pointerenter', this.#loadCartModules, { passive: true });
    trigger?.addEventListener('touchstart', this.#loadCartModules, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const trigger = this.querySelector('[data-testid="cart-drawer-trigger"]');
    trigger?.removeEventListener('pointerenter', this.#loadCartModules);
    trigger?.removeEventListener('touchstart', this.#loadCartModules);
  }

  #handleCartAdd = (event) => {
    if (event.detail.data?.didError) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#loadCartModules();
    }

    this.#syncCartItems(event).catch((error) => {
      console.error('Failed to sync cart drawer:', error);
    });
  };

  /**
   * Keeps the drawer contents in sync even before cart item modules finish lazy-loading.
   * @param {CartAddEvent} event
   */
  #syncCartItems = async (event) => {
    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId;
    if (!sectionId) return;

    const sectionHTML = event.detail.data?.sections?.[sectionId];
    if (sectionHTML) {
      await morphSection(sectionId, sectionHTML);
      this.#updateDialogState();
      return;
    }

    await sectionRenderer.renderSection(sectionId, { cache: false });
    this.#updateDialogState();
  };

  #updateDialogState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const cartItemCountText = dialog.querySelector('[ref="cartItemCount"]')?.textContent;
    const cartItemCount = cartItemCountText ? parseInt(cartItemCountText, 10) : 0;
    const isEmpty = cartItemCount === 0;

    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
    this.#updateStickyState();
    this.#celebrateFreeShipping();
  };

  #handleDialogOpen = () => {
    this.#celebrationPending = true;
    this.#updateStickyState();
    this.#celebrateFreeShipping();
  };

  #handleDialogClose = () => {
    this.#celebrationPending = false;
  };

  #celebrateFreeShipping = () => {
    if (!this.#celebrationPending) return;

    const badge = this.querySelector('[data-free-shipping-celebration]');
    if (!(badge instanceof HTMLElement)) return;

    this.#celebrationPending = false;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    badge.classList.remove('is-celebrating');
    void badge.offsetWidth;
    badge.classList.add('is-celebrating');
  };

  open() {
    this.showDialog();
    this.#loadCartModules();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #loadCartModules = () => {
    if (this.#cartModulesPromise) return this.#cartModulesPromise;

    const sources = [
      this.dataset.cartItemsSrc,
      this.dataset.cartQuantitySrc,
      this.dataset.volumePricingInfoSrc,
    ].filter(Boolean);

    this.#cartModulesPromise = Promise.all(sources.map((src) => import(src))).catch((error) => {
      this.#cartModulesPromise = null;
      throw error;
    });
    return this.#cartModulesPromise;
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
